import * as React from "react";
import { Badge as MantineBadge } from "@mantine/core";
import styled, { css, useTheme } from "styled-components";
import Icon from "components/common/Icon";
import sizeForMantine from "theme/utils/sizeForMantine";
export const BADGE_COLORS = [
  "primary",
  "danger",
  "success",
  "warning",
  "gray"
];
export const BADGE_VARIANTS = ["light", "filled"];
export const BADGE_SIZES = ["sm", "md", "lg"];
const mapStyle = (style, theme) => style === "default" ? theme.colors.button.gray.background : theme.colors.variant[style];
const mapFontSize = {
  xs: "tiny",
  sm: "small",
  md: "small",
  lg: "body"
};
const dotSize = {
  xs: "4px",
  sm: "4px",
  md: "6px",
  lg: "8px"
};
const iconSizeForBadge = {
  xs: "xs",
  sm: "xs",
  md: "xs",
  lg: "sm"
};
const StyledBadge = styled(MantineBadge)(
  ({ theme, $background, $color, size }) => css`
    text-transform: none;
    font-weight: 600;
    background-color: ${$background};
    color: ${$color};

    /* Let the badge shrink below its content width — as a flex/grid item (min-width: 0) and
       capped to its container (max-width: 100%) instead of Mantine's default width: fit-content.
       Without this the badge always stays as wide as its text, so the label below never gets a
       constrained width to truncate against. */
    min-width: 0;
    max-width: 100%;

    .mantine-Badge-label {
      display: flex;
      align-items: center;
      gap: ${theme.spacings.xxs};
      font-size: ${theme.fonts.size[mapFontSize[size]]};
      min-width: 0;
      overflow: hidden;
      text-overflow: ellipsis;
      white-space: nowrap;
    }

    &.uppercase {
      text-transform: uppercase;
    }
  `
);
const Dot = styled.span(
  ({ $color, $size }) => css`
    display: inline-block;
    flex-shrink: 0;
    width: ${dotSize[$size]};
    height: ${dotSize[$size]};
    border-radius: 50%;
    background-color: ${$color};
  `
);
const Badge = ({
  "aria-label": ariaLabel = void 0,
  bsSize = void 0,
  bsStyle = "default",
  className = void 0,
  children = void 0,
  color = void 0,
  "data-testid": dataTestid,
  dot = false,
  leftIcon = void 0,
  onClick = void 0,
  onMouseEnter = void 0,
  onMouseLeave = void 0,
  rightIcon = void 0,
  role = void 0,
  size = void 0,
  style = void 0,
  title = void 0,
  uppercase = false,
  variant = "light"
}, ref) => {
  const theme = useTheme();
  const resolvedSize = size != null ? size : bsSize ? sizeForMantine(bsSize) : "md";
  const background = color ? theme.colors.badges[color][variant].background : mapStyle(bsStyle, theme);
  const textColor = color ? theme.colors.badges[color][variant].text : theme.utils.contrastingColor(background);
  const iconSize = iconSizeForBadge[resolvedSize];
  let leftSection;
  if (color && dot) {
    leftSection = /* @__PURE__ */ React.createElement(Dot, { $color: textColor, $size: resolvedSize, "data-testid": "badge-dot" });
  } else if (leftIcon) {
    leftSection = /* @__PURE__ */ React.createElement(Icon, { name: leftIcon, size: iconSize });
  }
  const rightSection = rightIcon ? /* @__PURE__ */ React.createElement(Icon, { name: rightIcon, size: iconSize }) : void 0;
  const sharedProps = {
    "aria-label": ariaLabel,
    $background: background,
    $color: textColor,
    className: uppercase ? `${className != null ? className : ""} uppercase` : className,
    title,
    "data-testid": dataTestid,
    role,
    style,
    variant: "filled",
    leftSection,
    rightSection,
    onMouseEnter,
    onMouseLeave,
    size: resolvedSize
  };
  if (onClick) {
    return /* @__PURE__ */ React.createElement(
      StyledBadge,
      {
        ...sharedProps,
        style: { cursor: "pointer", ...style },
        component: "button",
        ref,
        onClick
      },
      children
    );
  }
  return /* @__PURE__ */ React.createElement(StyledBadge, { ...sharedProps, component: "span", ref }, children);
};
const ForwardedBadge = React.forwardRef(Badge);
ForwardedBadge.displayName = "Badge";
export default ForwardedBadge;
